import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Instagram, Phone, MessageCircle, Play, ArrowRight, ChevronLeft, ChevronRight, X, MapPin, Mail, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Brand logos
const brandLogos = [
  "/brands/nars-seeklogo.png",
  "/brands/fenty-beauty-seeklogo.png",
  "/brands/Charlotte_Tilbury_logo.svg.png",
  "/brands/givenchy-seeklogo.png",
  "/brands/Forever-21-Logo.png",
  "/brands/pngwing.com (1).png",
  "/brands/images.png",
  "/brands/images (1).png",
  "/brands/download (1).png"
];

// Coach Slider - Desktop Component
function CoachSliderDesktop({ pictures }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const defaultSlides = [
    "/coach-slider/sh1.jpg",
    "/coach-slider/sh2.jpg",
    "/coach-slider/sh3.jpg"
  ];
  
  // Filter out empty strings and use default if no valid pictures
  const validPictures = pictures && pictures.filter(pic => pic && pic.trim() !== '');
  const slides = validPictures && validPictures.length > 0 ? validPictures : defaultSlides;

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 2500); // 2.5 seconds

    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <div className="relative w-full h-full">
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={slide}
            alt={`Aradhana Shreya ${index + 1}`}
            className="w-full h-full object-cover"
          />
        </div>
      ))}
    </div>
  );
}

// Coach Slider - Mobile Component
function CoachSliderMobile({ pictures }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const defaultSlides = [
    "/coach-slider/mb1.jpg",
    "/coach-slider/mb2.jpg",
    "/coach-slider/mb3.jpg"
  ];
  
  // Filter out empty strings and use default if no valid pictures
  const validPictures = pictures && pictures.filter(pic => pic && pic.trim() !== '');
  const slides = validPictures && validPictures.length > 0 ? validPictures : defaultSlides;

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 2500); // 2.5 seconds

    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <div className="relative w-full h-full">
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={slide}
            alt={`Aradhana Shreya ${index + 1}`}
            className="w-full h-full object-cover"
          />
        </div>
      ))}
    </div>
  );
}

// Real Reviews Section Component
function RealReviewsSection() {
  const [reviewVideos, setReviewVideos] = useState([]);
  const [playingVideo, setPlayingVideo] = useState(null);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    fetchReviewVideos();
  }, []);

  const fetchReviewVideos = async () => {
    try {
      const response = await fetch(`${API}/reviews/videos`);
      const data = await response.json();
      setReviewVideos(data.videos || []);
    } catch (error) {
      console.error("Error fetching review videos:", error);
    }
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 320;
      const newScroll = direction === 'left' 
        ? scrollContainerRef.current.scrollLeft - scrollAmount
        : scrollContainerRef.current.scrollLeft + scrollAmount;
      
      scrollContainerRef.current.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });
    }
  };

  const handlePlayVideo = (video) => {
    if (!video.placeholder && video.video_base64) {
      setPlayingVideo(video);
    }
  };

  const closeVideoModal = () => {
    setPlayingVideo(null);
  };

  // Create placeholder videos if none uploaded
  const placeholderVideos = [
    { id: 'p1', reviewer_name: 'Happy Customer 1', placeholder: true },
    { id: 'p2', reviewer_name: 'Happy Customer 2', placeholder: true },
    { id: 'p3', reviewer_name: 'Happy Customer 3', placeholder: true },
    { id: 'p4', reviewer_name: 'Happy Customer 4', placeholder: true },
    { id: 'p5', reviewer_name: 'Happy Customer 5', placeholder: true },
    { id: 'p6', reviewer_name: 'Happy Customer 6', placeholder: true },
  ];

  const displayVideos = reviewVideos.length > 0 ? reviewVideos : placeholderVideos;

  return (
    <section className="py-16 bg-gradient-to-br from-pink-50 via-white to-purple-50 relative overflow-hidden">
      {/* Video Modal */}
      {playingVideo && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={closeVideoModal}
        >
          <button
            onClick={closeVideoModal}
            className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 z-50"
          >
            ✕
          </button>
          <div className="relative w-full max-w-4xl max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            <video
              src={playingVideo.video_base64}
              controls
              autoPlay
              playsInline
              webkit-playsinline="true"
              className="w-full h-full max-h-[80vh] object-contain rounded-lg"
            >
              Your browser does not support the video tag.
            </video>
            <p className="text-white text-center mt-4 text-xl font-bold">{playingVideo.reviewer_name}</p>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Title */}
        <div className="text-center mb-12">
          <h2 className="text-5xl md:text-6xl font-bold mb-4">
            <span className="bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">Real People, </span>
            <span className="text-black" style={{ fontSize: '90%' }}>Real Review</span>
          </h2>
          <div className="w-32 h-1 bg-yellow-500 mx-auto mb-4"></div>
          <p className="text-2xl md:text-3xl font-semibold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Real Stories</p>
        </div>

        {/* Desktop View - Scrollable Row */}
        <div className="hidden md:block relative">
          <div
            ref={scrollContainerRef}
            className="flex gap-6 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {displayVideos.map((video) => (
              <div
                key={video.id}
                className="flex-shrink-0 w-80 snap-center"
              >
                <div 
                  className="group relative overflow-hidden rounded-2xl shadow-2xl cursor-pointer transform transition-all duration-300 hover:scale-105"
                  onClick={() => handlePlayVideo(video)}
                >
                  <div className="relative h-96 bg-gradient-to-br from-purple-100 to-pink-100">
                    {video.placeholder ? (
                      // Placeholder design
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-8xl mb-4">🎥</div>
                          <p className="text-gray-600 font-semibold">Review Video</p>
                        </div>
                      </div>
                    ) : video.thumbnail_base64 ? (
                      <img
                        src={video.thumbnail_base64}
                        alt={video.reviewer_name}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <video
                        src={video.video_base64}
                        className="absolute inset-0 w-full h-full object-cover"
                        muted
                        playsInline
                        preload="metadata"
                      />
                    )}
                    
                    {/* Play Button Overlay */}
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center group-hover:bg-black/30 transition-all">
                      <div className="w-16 h-16 rounded-full bg-yellow-500 flex items-center justify-center group-hover:scale-110 transition-transform shadow-xl">
                        <Play className="w-8 h-8 text-white ml-1 fill-white" />
                      </div>
                    </div>

                    {/* Reviewer Name */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                      <p className="text-white font-bold text-center text-lg">{video.reviewer_name}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Arrows */}
          {displayVideos.length > 4 && (
            <>
              <button
                onClick={() => scroll('left')}
                className="absolute left-0 top-1/2 transform -translate-y-1/2 w-14 h-14 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center shadow-xl hover:from-pink-600 hover:to-purple-600 transition-all z-10"
              >
                <ChevronLeft className="w-7 h-7 text-white" />
              </button>
              <button
                onClick={() => scroll('right')}
                className="absolute right-0 top-1/2 transform -translate-y-1/2 w-14 h-14 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center shadow-xl hover:from-pink-600 hover:to-purple-600 transition-all z-10"
              >
                <ChevronRight className="w-7 h-7 text-white" />
              </button>
            </>
          )}

          {/* More Review Button - Desktop */}
          <div className="text-center mt-8">
            <Button
              size="lg"
              className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white px-12 py-6 text-lg font-bold rounded-full shadow-xl"
              onClick={() => window.location.href = '/more-reviews'}
            >
              More Review
            </Button>
          </div>
        </div>

        {/* Mobile View - Carousel */}
        <div className="md:hidden">
          <div
            className="flex gap-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory px-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {displayVideos.map((video) => (
              <div
                key={video.id}
                className="flex-shrink-0 w-64 snap-center"
              >
                <div 
                  className="group relative overflow-hidden rounded-3xl shadow-2xl cursor-pointer" 
                  style={{ border: '3px solid #FCD34D' }}
                  onClick={() => handlePlayVideo(video)}
                >
                  <div className="relative h-[450px] bg-gradient-to-br from-gray-800 to-gray-900">
                    {video.placeholder ? (
                      // Placeholder design
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-7xl mb-4">🎥</div>
                          <p className="text-gray-400 font-semibold">Review Video</p>
                        </div>
                      </div>
                    ) : video.thumbnail_base64 ? (
                      <img
                        src={video.thumbnail_base64}
                        alt={video.reviewer_name}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <video
                        src={video.video_base64}
                        className="absolute inset-0 w-full h-full object-cover"
                        muted
                        playsInline
                        preload="metadata"
                      />
                    )}
                    
                    {/* Play Button Overlay */}
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                      <div className="w-20 h-20 rounded-full bg-yellow-500 flex items-center justify-center shadow-2xl">
                        <Play className="w-10 h-10 text-black ml-1 fill-black" />
                      </div>
                    </div>

                    {/* Reviewer Name */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-transparent">
                      <p className="text-white font-bold text-center text-xl">{video.reviewer_name}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* View All Button */}
          <div className="text-center mt-8">
            <Button
              size="lg"
              className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white px-12 py-6 text-lg font-bold rounded-full shadow-xl"
              onClick={() => window.location.href = '/more-reviews'}
            >
              More Review
            </Button>
          </div>
        </div>
      </div>

      {/* Hide scrollbar */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
}

export default function NewHomePage() {
  const navigate = useNavigate();
  const [heroContent, setHeroContent] = useState({ video_base64: "", image_base64: "" });
  const [videoGallery, setVideoGallery] = useState([]);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const videoRef = useRef(null);
  
  // Login Modal States
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginType, setLoginType] = useState("student"); // student, teacher, admin
  const [loginForm, setLoginForm] = useState({ username: "", password: "", phone: "" });
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  // Footer Content State
  const [footerContent, setFooterContent] = useState({
    title: "MUAS Academy",
    subtitle: "Ranchi's Premier Makeup & Beauty Training Institute",
    phone: "9773682082",
    whatsapp: "919773682082",
    instagram: "https://www.instagram.com/aradhanashreyamakeup",
    address: "C-127, Harmu Housing Colony, Harmu Ranchi 834002",
    copyright: "© 2025 MUAS Academy. All rights reserved."
  });
  
  // Portfolio Box Images State
  const [portfolioImages, setPortfolioImages] = useState({
    class_videos_image: "",
    bride_portfolio_image: "",
    model_portfolio_image: ""
  });
  
  // Aradhana Section State
  const [aradhanaSection, setAradhanaSection] = useState({
    title: "Meet Your Mentors",
    subtitle: "Learn from the best in the industry",
    description: "",
    pictures: []
  });

  // Load hero content
  useEffect(() => {
    fetchHeroContent();
    fetchVideoGallery();
    fetchFooterContent();
    fetchPortfolioImages();
    fetchAradhanaSection();
  }, []);

  const fetchHeroContent = async () => {
    try {
      const response = await fetch(`${API}/homepage/hero`);
      const data = await response.json();
      setHeroContent(data);
    } catch (error) {
      console.error("Error fetching hero content:", error);
    }
  };

  const fetchVideoGallery = async () => {
    try {
      const response = await fetch(`${API}/homepage/videos`);
      const data = await response.json();
      setVideoGallery(data.videos || []);
    } catch (error) {
      console.error("Error fetching video gallery:", error);
    }
  };
  
  const fetchPortfolioImages = async () => {
  try {
    const response = await fetch(`${API}/homepage-sections`);
    if (!response.ok) {
      console.error("Portfolio images fetch failed:", response.status);
      return;
    }
    const data = await response.json();
    if (data) {
      setPortfolioImages(data);
    }
  } catch (error) {
    console.error("Error fetching portfolio images:", error);
  }
};

const fetchAradhanaSection = async () => {
  try {
    const response = await fetch(`${API}/aradhana-section`);
    if (!response.ok) {
      console.error("Aradhana section fetch failed:", response.status);
      return;
    }
    const data = await response.json();
    if (data) {
      setAradhanaSection({
        title: data.title || "Meet Your Mentors",
        subtitle: data.subtitle || "Learn from the best in the industry",
        description: data.description || "",
        pictures: data.pictures || []
      });
    }
  } catch (error) {
    console.error("Error fetching aradhana section:", error);
  }
};
  
  const fetchFooterContent = async () => {
    try {
      const response = await fetch(`${API}/footer-content`);
      const data = await response.json();
      if (data && data.title) {
        setFooterContent(data);
      }
    } catch (error) {
      console.error("Error fetching footer content:", error);
    }
  };
  
  const handleLogin = async () => {
    setIsLoggingIn(true);
    try {
      if (loginType === "admin") {
        const response = await axios.post(`${API}/admin/login`, {
          username: loginForm.username,
          password: loginForm.password
        });
        if (response.data.success) {
          localStorage.setItem("admin", "true");
          toast.success("Admin login successful!");
          setShowLoginModal(false);
          navigate("/admin");
        }
      } else if (loginType === "teacher") {
        const response = await axios.post(`${API}/teacher/login`, {
          phone: loginForm.phone,
          password: loginForm.password
        });
        if (response.data.success) {
          localStorage.setItem("teacher", JSON.stringify(response.data.teacher));
          toast.success("Teacher login successful!");
          setShowLoginModal(false);
          navigate("/admin");
        }
      } else {
        const response = await axios.post(`${API}/student/login`, {
          phone: loginForm.phone,
          password: loginForm.password
        });
        if (response.data.success) {
          localStorage.setItem("student", JSON.stringify(response.data.student));
          toast.success("Student login successful!");
          setShowLoginModal(false);
          navigate("/admin");
        }
      }
    } catch (error) {
      // Handle FastAPI validation errors (array) or standard error messages (string)
      const errorDetail = error.response?.data?.detail;
      let errorMessage = "Login failed. Please check your credentials.";
      if (typeof errorDetail === "string") {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail) && errorDetail.length > 0) {
        errorMessage = errorDetail.map(e => e.msg || e.message || String(e)).join(", ");
      }
      toast.error(errorMessage);
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Auto-play video when loaded
  useEffect(() => {
    if (videoRef.current && heroContent.video_base64) {
      videoRef.current.play().catch(err => console.log("Autoplay prevented:", err));
    }
  }, [heroContent.video_base64]);

  return (
    <div className="min-h-screen bg-white">
      {/* Sticky Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <a href="/" className="flex items-center gap-3">
              <img src="/logo.png" alt="MUAS Academy" className="w-10 h-10 rounded-full object-cover" />
              <span className="text-lg font-bold text-gray-800 hidden sm:block" style={{ fontFamily: "'Playfair Display', serif" }}>
                MUAS Academy
              </span>
            </a>
            
            {/* Navigation Links - Desktop */}
            <div className="hidden md:flex items-center gap-6">
              <a href="/" className="text-gray-600 hover:text-pink-600 text-sm font-medium transition-colors">Home</a>
              <a href="/about-us" className="text-gray-600 hover:text-pink-600 text-sm font-medium transition-colors">About</a>
              <a href="/courses" className="text-gray-600 hover:text-pink-600 text-sm font-medium transition-colors">Courses</a>
              <a href="/bridal-services" className="text-gray-600 hover:text-pink-600 text-sm font-medium transition-colors">Bridal Services</a>
              <a href="/bridal-portfolio" className="text-gray-600 hover:text-pink-600 text-sm font-medium transition-colors">Gallery</a>
              <Button
                size="sm"
                className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white"
                onClick={() => setShowLoginModal(true)}
              >
                Login
              </Button>
            </div>
            
            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setShowLoginModal(true)}
            >
              Login
            </Button>
          </div>
        </div>
      </nav>

      {/* Video Hero Section */}
      <section className="relative w-full h-screen overflow-hidden pt-16">
        {/* Video Background */}
        {heroContent.video_base64 ? (
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            webkit-playsinline="true"
            x5-playsinline="true"
            x5-video-player-type="h5"
            x5-video-player-fullscreen="true"
            className="absolute inset-0 w-full h-full object-cover"
            onLoadedData={() => setIsVideoLoaded(true)}
            onError={(e) => console.error("Video load error:", e)}
          >
            <source src={heroContent.video_base64} type="video/mp4" />
            <source src={heroContent.video_base64} type="video/webm" />
            <source src={heroContent.video_base64} type="video/quicktime" />
          </video>
        ) : heroContent.image_base64 ? (
          <img
            src={heroContent.image_base64}
            alt="Hero"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-pink-400 via-purple-500 to-pink-600"></div>
        )}

        {/* Overlay */}
        <div className="absolute inset-0 bg-black/30"></div>

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col items-center justify-end pb-32 text-white px-4">
          <p className="text-2xl md:text-3xl mb-12 text-center max-w-2xl drop-shadow-lg font-semibold">
            Choose Your Service
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              size="lg"
              className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white text-lg px-8 py-6 shadow-2xl"
              onClick={() => navigate("/bridal-services")}
            >
              I Want Bride Makeup
            </Button>
            <Button
              size="lg"
              className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white text-lg px-8 py-6 shadow-2xl"
              onClick={() => navigate("/courses")}
            >
              I Want Makeup Course
            </Button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2"></div>
          </div>
        </div>
      </section>

      {/* Brand Logo Strip */}
      <section className="py-12 bg-gradient-to-r from-pink-50 to-purple-50 overflow-hidden">
        <div className="mb-6 text-center">
          <h2 className="text-2xl font-bold text-gray-800">Trusted Brands We Use</h2>
        </div>
        <div className="relative">
          <div className="flex animate-scroll-left">
            {[...brandLogos, ...brandLogos].map((logo, index) => (
              <div
                key={index}
                className="flex-shrink-0 w-40 h-24 mx-6 flex items-center justify-center bg-white rounded-lg shadow-md"
              >
                <img
                  src={logo}
                  alt="Brand"
                  className="max-w-full max-h-full object-contain p-4"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Connect Bar */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-8">Connect With Us</h2>
          <div className="flex flex-wrap justify-center items-center gap-6">
            {/* Instagram Button */}
            <a
              href="https://www.instagram.com/aradhanashreyamakeup?igsh=eWhrMm44MnhrOG83"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-transform hover:scale-105"
            >
              <img
                src="/instagram-new.jpg"
                alt="Follow us on Instagram"
                className="h-20 w-auto cursor-pointer rounded-lg shadow-md"
              />
            </a>

            {/* WhatsApp Button */}
            <a
              href="https://wa.me/919773682082?text=Hello, I'm interested in your services"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-transform hover:scale-105"
            >
              <img
                src="/whatsapp-new.jpg"
                alt="Chat with us on WhatsApp"
                className="h-20 w-auto cursor-pointer rounded-lg shadow-md"
              />
            </a>

            {/* Call Button */}
            <a
              href="tel:9773682082"
              className="transition-transform hover:scale-105"
            >
              <img
                src="/call-new.jpg"
                alt="Call us"
                className="h-20 w-auto cursor-pointer rounded-lg shadow-md"
              />
            </a>
          </div>
        </div>
      </section>

      {/* Meet Your Coach Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          {/* Top Strip with Name */}
          <div className="bg-[#FFE0E6] py-8 px-6 rounded-2xl mb-12 max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
                {aradhanaSection.title || "ARADHANA SHREYA"}
              </h2>
              {/* Desktop view - single line */}
              <div className="hidden md:block px-6 py-3 bg-white rounded-full">
                <p className="text-black font-semibold text-base md:text-lg text-center">
                  {aradhanaSection.subtitle || "Makeup Artist & Head MUAS Academy"}
                </p>
              </div>
              {/* Mobile view - two lines */}
              <div className="md:hidden px-6 py-3 bg-white rounded-full">
                <p className="text-black font-semibold text-base text-center">
                  {aradhanaSection.subtitle || "Makeup Artist & Head MUAS Academy"}
                </p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Left Side - Text Content */}
            <div className="space-y-6 order-2 md:order-1">
              <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
                {aradhanaSection.description ? (
                  <div dangerouslySetInnerHTML={{ __html: aradhanaSection.description.replace(/\n/g, '<br/>') }} />
                ) : (
                  <>
                    <p>
                      For the past 10+ years, I've been on a mission to help aspiring makeup artists 
                      transform their passion into a thriving career. I don't just teach theory. 
                      I bring real & tested techniques that have worked for hundreds of students 
                      across Ranchi and beyond.
                    </p>
                    
                    <p>
                      With a dedicated team of experienced makeup professionals, we've trained 
                      students across diverse backgrounds and helped them launch successful careers 
                      with expert-led training, proven techniques, and hands-on practice with 
                      real clients.
                    </p>

                    <p>
                      From bridal makeup to party looks, from basic to advanced techniques, 
                      we cover it all. Our students don't just learn makeup - they learn the 
                      art of building confidence, understanding clients, and creating magic 
                      with every brush stroke.
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Right Side - Images (Desktop) - Auto Slider */}
            <div className="relative h-[600px] hidden md:block order-1 md:order-2 overflow-hidden rounded-3xl shadow-2xl">
              <CoachSliderDesktop pictures={aradhanaSection.pictures} />
            </div>

            {/* Mobile View - Auto Slider */}
            <div className="md:hidden relative h-[400px] w-full order-1 md:order-2 overflow-hidden rounded-3xl shadow-2xl">
              <CoachSliderMobile pictures={aradhanaSection.pictures} />
            </div>
          </div>
        </div>
      </section>

      {/* See Our Work Section */}
      <section className="py-16 bg-gradient-to-br from-pink-50 via-white to-purple-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
            See Our Work
          </h2>
          <p className="text-center text-gray-600 mb-12 text-lg">
            Explore our professional work and training sessions
          </p>

          <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {/* Class Videos Card */}
            <div
              onClick={() => navigate("/class-videos")}
              className="group relative overflow-hidden rounded-2xl shadow-2xl cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-3xl"
              data-testid="class-videos-card"
            >
              <div className="h-96 relative">
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: portfolioImages.class_videos_image ? `url(${portfolioImages.class_videos_image})` : 'url(/class-video-bg.jpg)' }}
                ></div>
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20"></div>

                {/* Content */}
                <div className="relative z-10 h-full flex flex-col items-center justify-end text-white p-8 pb-12">
                  <h3 className="text-3xl font-bold mb-4 text-center">Class Videos</h3>
                  <p className="text-center text-white/90 mb-6 text-sm">
                    Watch our professional makeup training sessions
                  </p>
                  <Button 
                    size="lg"
                    className="bg-white text-purple-600 hover:bg-purple-50 font-semibold group-hover:scale-110 transition-transform"
                  >
                    View Videos
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Real Bride Makeup Card */}
            <div
              onClick={() => navigate("/bridal-portfolio")}
              className="group relative overflow-hidden rounded-2xl shadow-2xl cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-3xl"
              data-testid="bride-portfolio-card"
            >
              <div className="h-96 relative">
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: portfolioImages.bride_portfolio_image ? `url(${portfolioImages.bride_portfolio_image})` : 'url(/bride-portfolio-bg.jpg)' }}
                ></div>
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20"></div>

                {/* Content */}
                <div className="relative z-10 h-full flex flex-col items-center justify-end text-white p-8 pb-12">
                  <h3 className="text-3xl font-bold mb-4 text-center">Real Bride Makeup</h3>
                  <p className="text-center text-white/90 mb-6 text-sm">
                    Discover our stunning bride makeup
                  </p>
                  <Button 
                    size="lg"
                    className="bg-white text-pink-600 hover:bg-pink-50 font-semibold group-hover:scale-110 transition-transform"
                  >
                    View Portfolio
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Model Portfolio Card */}
            <div
              onClick={() => navigate("/model-portfolio")}
              className="group relative overflow-hidden rounded-2xl shadow-2xl cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-3xl"
              data-testid="model-portfolio-card"
            >
              <div className="h-96 relative">
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: portfolioImages.model_portfolio_image ? `url(${portfolioImages.model_portfolio_image})` : 'url(/model-portfolio-bg.jpg)' }}
                ></div>
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20"></div>

                {/* Content */}
                <div className="relative z-10 h-full flex flex-col items-center justify-end text-white p-8 pb-12">
                  <h3 className="text-3xl font-bold mb-4 text-center">Model Portfolio</h3>
                  <p className="text-center text-white/90 mb-6 text-sm">
                    Explore our fashion and editorial makeup looks
                  </p>
                  <Button 
                    size="lg"
                    className="bg-white text-indigo-600 hover:bg-indigo-50 font-semibold group-hover:scale-110 transition-transform"
                  >
                    View Portfolio
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Real People, Real Review Section */}
      <RealReviewsSection />

      {/* What Do You Want Section - Our Work Portfolio */}
      <section className="py-16 bg-gradient-to-br from-pink-50 via-white to-purple-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
            What Do You Want?
          </h2>
          <p className="text-center text-black mb-12 text-lg max-w-4xl mx-auto">
            If you need the best makeup course in Ranchi or top makeup artist in Ranchi, MUAS Academy is your Destination.
          </p>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* I Want Makeup Course Card */}
            <div
              onClick={() => navigate("/courses")}
              className="group relative overflow-hidden rounded-2xl shadow-2xl cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-3xl"
            >
              <div className="h-[450px] relative">
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: 'url(/class-video-bg.jpg)' }}
                ></div>
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20"></div>

                {/* Content */}
                <div className="relative z-10 h-full flex flex-col items-center justify-end text-white p-8 pb-12">
                  <Button 
                    size="lg"
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold text-xl px-12 py-8 rounded-full shadow-xl group-hover:scale-110 transition-transform"
                  >
                    I Want Makeup Course
                  </Button>
                </div>
              </div>
            </div>

            {/* I Want Bride Makeup Card */}
            <div
              onClick={() => navigate("/bridal-services")}
              className="group relative overflow-hidden rounded-2xl shadow-2xl cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-3xl"
            >
              <div className="h-[450px] relative">
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: 'url(/bride-portfolio-bg.jpg)' }}
                ></div>
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20"></div>

                {/* Content */}
                <div className="relative z-10 h-full flex flex-col items-center justify-end text-white p-8 pb-12">
                  <Button 
                    size="lg"
                    className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-bold text-xl px-12 py-8 rounded-full shadow-xl group-hover:scale-110 transition-transform"
                  >
                    I Want Bride Makeup
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Location Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12 bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
            Our Location
          </h2>

          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto items-center">
            {/* Left Side - Address Text */}
            <div className="space-y-4">
              <p className="text-black text-xl">
                We are located at
              </p>
              <h3 className="text-black text-4xl md:text-5xl font-black leading-tight">
                Sahajanand Chowk, Harmu, Ranchi
              </h3>
              <div className="pt-4">
                <p className="text-gray-600 text-sm underline mb-2">
                  Address
                </p>
                <p className="text-gray-700 text-base">
                  C-127, Harmu Housing Colony, Harmu Ranchi 834002
                </p>
              </div>
            </div>

            {/* Right Side - Google Map (Desktop) */}
            <div className="hidden md:block rounded-2xl overflow-hidden shadow-2xl h-96">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3663.0779413928234!2d85.30622931496908!3d23.344135484786746!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39f4e1e5e5e5e5e5%3A0x573656250116139851!2sMUAS%20Academy!5e0!3m2!1sen!2sin!4v1234567890!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="MUAS Academy Location"
              ></iframe>
            </div>

            {/* Mobile View - CTA Button */}
            <div className="md:hidden text-center">
              <Button
                size="lg"
                className="bg-black hover:bg-gray-800 text-white font-bold text-lg px-12 py-6 rounded-full shadow-xl"
                onClick={() => window.open("https://share.google/mAeU7ULFywbXvrOfK", "_blank")}
              >
                Check Google Location
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowLoginModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Login</h2>
              <button onClick={() => setShowLoginModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Login Type Tabs */}
            <div className="flex gap-2 mb-6">
              {["student", "teacher", "admin"].map((type) => (
                <button
                  key={type}
                  onClick={() => { setLoginType(type); setLoginForm({ username: "", password: "", phone: "" }); }}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium capitalize transition-all ${
                    loginType === type 
                      ? "bg-gradient-to-r from-pink-500 to-purple-500 text-white" 
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
            
            {/* Login Form */}
            <div className="space-y-4">
              {loginType === "admin" ? (
                <div>
                  <Label>Username</Label>
                  <Input
                    type="text"
                    placeholder="Enter username"
                    value={loginForm.username}
                    onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                    className="mt-1"
                  />
                </div>
              ) : (
                <div>
                  <Label>Phone Number</Label>
                  <Input
                    type="tel"
                    placeholder="Enter phone number"
                    value={loginForm.phone}
                    onChange={(e) => setLoginForm({ ...loginForm, phone: e.target.value })}
                    className="mt-1"
                  />
                </div>
              )}
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  placeholder="Enter password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  className="mt-1"
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                />
              </div>
              <Button
                className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white py-5"
                onClick={handleLogin}
                disabled={isLoggingIn}
              >
                {isLoggingIn ? "Logging in..." : "Login"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Footer - Professional 4-Column Layout */}
      <footer className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white">
        {/* Main Footer Content */}
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            
            {/* Column 1 - Brand */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <img src="/logo.png" alt="MUAS Academy Logo" className="w-14 h-14 rounded-full object-cover" />
                <h3 className="text-2xl font-bold bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent" style={{ fontFamily: "'Playfair Display', serif" }}>
                  MUAS Academy
                </h3>
              </div>
              <p className="text-pink-300 text-sm font-medium">Ranchi's Premier Makeup & Beauty Training Institute</p>
              <p className="text-gray-400 text-sm leading-relaxed">
                Transform your passion into a profession. We offer comprehensive makeup courses, bridal training, and beauty education with industry-recognized certification.
              </p>
              <div className="flex gap-3 pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/10 p-2"
                  onClick={() => window.open("https://www.instagram.com/aradhanashreyamakeup/?hl=en", "_blank")}
                >
                  <Instagram className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/10 p-2"
                  onClick={() => window.open("https://wa.me/916201542274", "_blank")}
                >
                  <MessageCircle className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/10 p-2"
                  onClick={() => window.open("tel:+916201542274")}
                >
                  <Phone className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Column 2 - Quick Links */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/" className="text-gray-400 hover:text-pink-400 transition-colors">Home</a></li>
                <li><a href="/about-us" className="text-gray-400 hover:text-pink-400 transition-colors">About Us</a></li>
                <li><a href="/courses" className="text-gray-400 hover:text-pink-400 transition-colors">Courses</a></li>
                <li><a href="/bridal-services" className="text-gray-400 hover:text-pink-400 transition-colors">Bridal Services</a></li>
                <li><a href="/bridal-portfolio" className="text-gray-400 hover:text-pink-400 transition-colors">Gallery</a></li>
                <li>
                  <button 
                    onClick={() => setShowLoginModal(true)}
                    className="text-gray-400 hover:text-pink-400 transition-colors"
                  >
                    Student/Teacher/Admin Login
                  </button>
                </li>
              </ul>
            </div>

            {/* Column 3 - Contact Info */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white">Contact Info</h4>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-pink-400 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-400">
                    C-127, Harmu Housing Colony,<br />
                    Harmu Ranchi 834002,<br />
                    Jharkhand, India
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-pink-400 flex-shrink-0" />
                  <div className="text-gray-400">
                    <a href="tel:+916201542274" className="hover:text-pink-400 transition-colors">+91 6201542274</a><br />
                    <a href="tel:+919773682082" className="hover:text-pink-400 transition-colors">+91 9773682082</a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-pink-400 flex-shrink-0" />
                  <a href="mailto:muasacademy.ranchi@gmail.com" className="text-gray-400 hover:text-pink-400 transition-colors">
                    muasacademy.ranchi@gmail.com
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-pink-400 flex-shrink-0" />
                  <p className="text-gray-400">
                    Mon - Sat: 10:00 AM - 7:00 PM
                  </p>
                </div>
              </div>
            </div>

            {/* Column 4 - Legal */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/terms-and-conditions" className="text-gray-400 hover:text-pink-400 transition-colors">Terms & Conditions</a></li>
                <li><a href="/privacy-policy" className="text-gray-400 hover:text-pink-400 transition-colors">Privacy Policy</a></li>
                <li><a href="/refund-policy" className="text-gray-400 hover:text-pink-400 transition-colors">Refund & Cancellation Policy</a></li>
              </ul>
              
              {/* Login Button */}
              <div className="pt-4">
                <Button
                  className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white text-sm"
                  onClick={() => setShowLoginModal(true)}
                >
                  Login Portal
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Google Map */}
        <div className="px-4 pb-8">
          <div className="container mx-auto">
            <div className="rounded-xl overflow-hidden shadow-lg">
              <iframe 
                src="https://www.google.com/maps?q=C-127,+Harmu+Housing+Colony,+Harmu+Ranchi+834002&output=embed"
                width="100%" 
                height="250" 
                style={{ border: 0 }}
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="MUAS Academy Location"
              ></iframe>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700">
          <div className="container mx-auto px-4 py-4">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
              <p className="text-gray-400">
                © {new Date().getFullYear()} MUAS Academy. All rights reserved.
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-gray-400">
                <a href="/terms-and-conditions" className="hover:text-pink-400 transition-colors">Terms</a>
                <span className="hidden md:inline">|</span>
                <a href="/privacy-policy" className="hover:text-pink-400 transition-colors">Privacy</a>
                <span className="hidden md:inline">|</span>
                <a href="/refund-policy" className="hover:text-pink-400 transition-colors">Refund Policy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes scroll-left {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }

        .animate-scroll-left {
          animation: scroll-left 30s linear infinite;
        }

        .animate-scroll-left:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
}
